import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { App, Button, Layout, Segmented, Switch, Tooltip } from 'antd';
import {
  ContactsTwoTone,
  CopyOutlined,
  ExportOutlined,
  LayoutTwoTone,
  LogoutOutlined,
  MailTwoTone,
  QuestionCircleTwoTone,
  ScheduleTwoTone,
  SettingTwoTone,
} from '@ant-design/icons';
import NavigationBar from '../../../components/NavigationBar';
import DOTheme from '../../../components/directOrders/doTheme';
import dynamicFormsAdminService from '../../../services/dynamicFormsAdminService';
import dynamicFormsService from '../../../services/dynamicFormsService';
import languageService from '../../../services/languageService';
import ResponsesView from '../../../components/dynamicForms/ResponsesView';
import QuestionsBuilder from '../../../components/dynamicForms/QuestionsBuilder';
import DesignEditor from '../../../components/dynamicForms/DesignEditor';
import EmailsEditor from '../../../components/dynamicForms/EmailsEditor';
import WorkshopPlaces from '../../../components/dynamicForms/WorkshopPlaces';
import SettingsPanel from '../../../components/dynamicForms/SettingsPanel';
import { truthy } from '../../../components/dynamicForms/fieldTypes';
import { PORTAL_ORIGIN } from '../../../config';
import '../../../styles/dynamicForms.css';

const tone = '#2748d9';

const SECTIONS = {
  responses: { label: 'Responses', title: 'Responses', icon: <ContactsTwoTone twoToneColor={tone} /> },
  questions: { label: 'Questions', title: 'Questions', icon: <QuestionCircleTwoTone twoToneColor={tone} /> },
  design: { label: 'Design', title: 'Design', icon: <LayoutTwoTone twoToneColor={tone} /> },
  emails: { label: 'Emails', title: 'Emails', icon: <MailTwoTone twoToneColor={tone} />, managers: true },
  places: { label: 'Workshop places', title: 'Workshop places', icon: <ScheduleTwoTone twoToneColor={tone} /> },
  settings: { label: 'Settings', title: 'Settings', icon: <SettingTwoTone twoToneColor={tone} />, managers: true },
};

/**
 * A dynamic form's own workspace (direction A): a rail with whether the form is open, its link,
 * its language and its sections, and the section on the white sheet. Everything comes from the
 * existing endpoints; nothing here needs a backend change.
 */
const FormWorkspace = ({ formId, formName, slug, canDelete, canManageForms, onLogout }) => {
  const { message } = App.useApp();
  const [params, setParams] = useSearchParams();

  const [fields, setFields] = useState([]);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  const [layout, setLayout] = useState(null);
  const [language, setLanguage] = useState(null);
  const [languageBusy, setLanguageBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const [responses, setResponses] = useState(null);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [workshops, setWorkshops] = useState(null);
  const [workshopsLoading, setWorkshopsLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const loadFields = useCallback(async () => {
    try {
      const response = await dynamicFormsAdminService.getFields(formId);
      const list = response.success && Array.isArray(response.fields) ? response.fields : [];
      setFields(list);
      return list;
    } catch {
      message.error('Could not load the questions');
      return null;
    } finally {
      setFieldsLoaded(true);
    }
  }, [formId, message]);

  const loadLayout = useCallback(async () => {
    try {
      const response = await dynamicFormsService.getLayout(formId);
      setLayout(response.success ? response.layout || null : null);
    } catch {
      message.error('Could not load the design');
    }
  }, [formId, message]);

  const loadLanguage = useCallback(async () => {
    try {
      const response = await languageService.getFormLanguage(formId);
      if (response.success) setLanguage(response.form);
    } catch {
      // the card falls back to "Application" / "Submit Application", as on the page
    }
  }, [formId]);

  const loadMeta = useCallback(async () => {
    try {
      let formSlug = slug;
      if (!formSlug) {
        const basic = await dynamicFormsAdminService.getForm(formId);
        formSlug = basic.form?.form_slug;
        if (!formSlug) {
          setMeta({ ...basic.form, is_active: truthy(basic.form?.is_active) });
          return;
        }
      }
      const complete = await dynamicFormsService.getFormBySlug(formSlug);
      setMeta(complete.form);
    } catch {
      message.error('Could not load the form settings');
    }
  }, [formId, slug, message]);

  const loadResponses = useCallback(async () => {
    try {
      setResponsesLoading(true);
      const response = await dynamicFormsAdminService.exportSubmissions(formId);
      if (response.success) setResponses({ data: response.data || [], total: response.total ?? (response.data || []).length });
    } catch {
      message.error('Could not load the responses');
    } finally {
      setResponsesLoading(false);
    }
  }, [formId, message]);

  const loadWorkshops = useCallback(async () => {
    try {
      setWorkshopsLoading(true);
      const response = await dynamicFormsService.getWorkshops(formId);
      if (response.success) setWorkshops(response.workshop_fields || []);
    } catch {
      message.error('Could not load the workshop places');
    } finally {
      setWorkshopsLoading(false);
    }
  }, [formId, message]);

  useEffect(() => {
    if (!formId) return;
    loadFields();
    loadLayout();
    loadLanguage();
    loadMeta();
    loadResponses();
    loadWorkshops();
  }, [formId, loadFields, loadLayout, loadLanguage, loadMeta, loadResponses, loadWorkshops]);

  // Questions changes can add or remove limited places
  const reloadFields = useCallback(async () => {
    const list = await loadFields();
    loadWorkshops();
    return list;
  }, [loadFields, loadWorkshops]);

  const reloadResponses = useCallback(() => {
    loadResponses();
    loadWorkshops();
  }, [loadResponses, loadWorkshops]);

  const formSlug = meta?.form_slug || slug;
  const liveUrl = formSlug ? `${PORTAL_ORIGIN}/client-portal/${formSlug}` : null;
  const isOpen = truthy(meta?.is_active);
  const fullCount = (workshops || []).reduce((n, f) => n + f.workshops.filter((w) => w.is_full).length, 0);
  const hasPlaces = (workshops || []).length > 0;
  const liveIds = useMemo(() => (responses ? new Set(responses.data.map((r) => r.submission_id)) : null), [responses]);

  const available = Object.keys(SECTIONS).filter((key) => (!SECTIONS[key].managers || canManageForms) && (key !== 'places' || hasPlaces));
  const requested = params.get('section');
  const section = available.includes(requested) ? requested : 'responses';
  const [visited, setVisited] = useState(() => new Set([section]));
  useEffect(() => {
    setVisited((v) => (v.has(section) ? v : new Set(v).add(section)));
  }, [section]);

  const go = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'responses') next.delete('section');
    else next.set('section', key);
    setParams(next, { replace: false });
  };

  const counts = {
    responses: responses ? responses.total.toLocaleString() : null,
    questions: fieldsLoaded ? String(fields.length) : null,
    places: fullCount ? `${fullCount} full` : null,
  };

  const toggleOpen = async (next) => {
    try {
      setToggling(true);
      const response = await dynamicFormsAdminService.toggleFormActive(formId, next ? 1 : 0);
      if (!response.success) throw new Error(response.error);
      message.success(next ? 'Form opened' : 'Form closed');
      await loadMeta();
    } catch (err) {
      message.error(err.message || 'Could not change whether the form is open');
    } finally {
      setToggling(false);
    }
  };

  const changeLanguage = async (languageId) => {
    try {
      setLanguageBusy(true);
      const response = await languageService.toggleFormLanguage(formId, languageId);
      if (response.success) {
        setLanguage(response.form);
        message.success(`Form language set to ${languageId === 2 ? 'English' : 'Maltese'}`);
      }
    } catch {
      message.error('Could not change the language');
    } finally {
      setLanguageBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(liveUrl);
      message.success('Link copied');
    } catch {
      message.error('Could not copy the link');
    }
  };

  const subs = {
    responses: 'Everyone who has sent the form. Open a response to see every answer.',
    questions: 'Pick a question to edit it. The preview is the real form page.',
    design: 'The logo, titles, banner and instructions around the questions.',
    emails: 'What applicants, reviewers and your team receive when someone sends the form.',
    places: 'Places booked and left for each option with limited places.',
    settings: 'Whether the form is open, the payment step and the language.',
  };

  const render = (key) => {
    switch (key) {
      case 'responses':
        return (
          <ResponsesView
            formId={formId}
            formName={formName}
            fields={fields}
            responses={responses}
            loading={responsesLoading}
            onReload={reloadResponses}
            canDelete={canDelete}
          />
        );
      case 'questions':
        return (
          <QuestionsBuilder
            formId={formId}
            fields={fields}
            onFieldsChange={reloadFields}
            canEdit={canManageForms}
            language={language}
            layout={layout}
            liveUrl={liveUrl}
          />
        );
      case 'design':
        return (
          <DesignEditor
            formId={formId}
            layout={layout}
            onLayoutChange={loadLayout}
            canEdit={canManageForms}
            language={language}
            fields={fields}
            liveUrl={liveUrl}
          />
        );
      case 'emails':
        return <EmailsEditor formId={formId} fields={fields} />;
      case 'places':
        return <WorkshopPlaces formId={formId} workshopFields={workshops} loading={workshopsLoading} onReload={loadWorkshops} liveIds={liveIds} />;
      case 'settings':
        return (
          <SettingsPanel
            formId={formId}
            formName={formName}
            meta={meta}
            onMetaChange={loadMeta}
            language={language}
            onLanguageChange={changeLanguage}
            languageBusy={languageBusy}
            liveUrl={liveUrl}
          />
        );
      default:
        return null;
    }
  };

  return (
    <DOTheme>
      <Layout className="do-shell do-scope dfw-page">
        <NavigationBar />
        <main className="do-main dfw-main">
          <aside className="dfw-rail">
            <div className="dfw-rail__head">
              <h1 className="dfw-rail__name">{formName}</h1>
              <div className="dfw-rail__status">
                <span className={`dfw-status${isOpen ? '' : ' is-closed'}`}>{meta ? (isOpen ? 'Open' : 'Closed') : '…'}</span>
                {canManageForms && meta && (
                  <Tooltip title={isOpen ? 'Close the form to new responses' : 'Open the form to responses'}>
                    <Switch size="small" checked={isOpen} onChange={toggleOpen} loading={toggling} aria-label={isOpen ? 'Close the form' : 'Open the form'} />
                  </Tooltip>
                )}
              </div>
              {liveUrl && (
                <div className="dfw-rail__link">
                  <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="dfw-rail__url" title={liveUrl}>
                    {liveUrl.replace(/^https?:\/\//, '')}
                  </a>
                  <span className="dfw-rail__linkbtns">
                    <Tooltip title="Copy link">
                      <Button size="small" type="text" icon={<CopyOutlined aria-hidden="true" />} onClick={copyLink} aria-label="Copy the form link" />
                    </Tooltip>
                    <Tooltip title="Open the live form">
                      <Button size="small" type="text" icon={<ExportOutlined aria-hidden="true" />} href={liveUrl} target="_blank" rel="noopener noreferrer" aria-label="Open the live form" />
                    </Tooltip>
                  </span>
                </div>
              )}
            </div>

            <nav className="dfw-rail__nav" aria-label="Form sections">
              {available.map((key) => (
                <button
                  type="button"
                  key={key}
                  className={`dfw-rail__item${section === key ? ' is-on' : ''}${key === 'places' && fullCount ? ' is-accent' : ''}`}
                  aria-current={section === key ? 'page' : undefined}
                  onClick={() => go(key)}
                >
                  <span className="dfw-rail__icon" aria-hidden="true">{SECTIONS[key].icon}</span>
                  <span className="dfw-rail__text">{SECTIONS[key].label}</span>
                  {counts[key] && <span className="dfw-rail__count dfw-num">{counts[key]}</span>}
                </button>
              ))}
            </nav>

            <div className="dfw-rail__foot">
              <div className="dfw-rail__lang">
                <span>Language</span>
                {canManageForms ? (
                  <Segmented
                    size="small"
                    value={language?.language_id === 2 ? 2 : 1}
                    onChange={changeLanguage}
                    disabled={languageBusy || !language}
                    options={[{ value: 1, label: 'MT' }, { value: 2, label: 'EN' }]}
                    aria-label="Form language"
                  />
                ) : (
                  <strong>{language?.language_id === 2 ? 'English' : 'Maltese'}</strong>
                )}
              </div>
              <Button className="dfw-rail__logout" type="text" icon={<LogoutOutlined aria-hidden="true" />} onClick={onLogout}>
                Log out of client admin
              </Button>
            </div>
          </aside>

          <div className="dfw-sheet">
            <header className="dfw-head">
              <h2 className="dfw-title">{SECTIONS[section].title}</h2>
              <p className="dfw-sub">{subs[section]}</p>
            </header>
            {available.map((key) =>
              visited.has(key) ? (
                <div key={key} hidden={key !== section} className="dfw-section">
                  {render(key)}
                </div>
              ) : null
            )}
          </div>
        </main>
      </Layout>
    </DOTheme>
  );
};

export default FormWorkspace;
